/*
  Header cart-count badge updater.

  ─── Why this file dispatches on TWO channels ─────────────────────────────
  Two event systems coexist in this theme. Do not collapse them into one,
  even though the duplication looks redundant at first glance.

    1. Dawn's internal pub/sub (`assets/pubsub.js`)
       Channel name: 'cart-update' (PUB_SUB_EVENTS.cartUpdate)
       Used by:      Dawn's own cart-drawer.js, cart-notification.js,
                     cart.js (cart page) — they publish here when the cart
                     mutates via /cart/*.js endpoints.
       Scope:        relies on globals (`subscribe`, `publish`,
                     `PUB_SUB_EVENTS`) exposed by pubsub.js + constants.js
                     loading before this script via classic-script tag order.

    2. DOM CustomEvent on `document`
       Channel name: 'cart:updated' (per SHOPIFY-HANDOFF.md section 2.3)
       Used by:      our own code (Phase 4 cart drawer, future ATC buttons,
                     analytics, A/B testing snippets).
       Scope:        anything on the page can `addEventListener` for it.

  This file BRIDGES them: it subscribes to Dawn's pub/sub channel (so when
  Dawn mutates the cart, we hear it) and re-broadcasts as a DOM CustomEvent
  (so our non-Dawn code hears it too). It also updates [data-cart-count]
  text directly — the badge is the one consumer that has to work no matter
  which channel fired.

  If you delete one of the two dispatches, half the theme will stop seeing
  cart updates. The pub/sub side is needed for Dawn; the DOM side is needed
  for spec compliance and our own code.
  ───────────────────────────────────────────────────────────────────────────
*/

(() => {
  const updateBadge = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.toggleAttribute('hidden', count === 0);
    });
  };

  const broadcast = (detail) => {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  };

  // 1. Subscribe to Dawn's pub/sub for in-theme cart mutations.
  //    `subscribe` and `PUB_SUB_EVENTS` are globals from pubsub.js + constants.js.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
      if (!cartData) return;
      updateBadge(cartData.item_count ?? 0);
      broadcast(cartData);
    });
  }

  // 2. Listen on the DOM for callers outside Dawn's pubsub (Phase 4 drawer,
  //    custom ATC handlers, etc.). Update the badge but DON'T re-broadcast
  //    — avoids an infinite loop.
  document.addEventListener('cart:updated', (e) => {
    const data = e.detail;
    if (!data) return;
    updateBadge(data.item_count ?? 0);
  });
})();
